/* ==========================================================================
   WAYPOINT — tiny typed fetch client. One function per docs/API.md endpoint.
   ========================================================================== */

import type {
  Activity,
  ApiErrorBody,
  ApproveProposalResult,
  AuthUser,
  AuthUserSummary,
  ChatMessageDto,
  ChatUnreadResponse,
  CreateActivityInput,
  CreateMemoryInput,
  MealPreset,
  ShoppingList,
  CreateShareInput,
  CreateStopInput,
  CreateTripInput,
  GuestbookEntry,
  GuestbookEntryInput,
  HealthResponse,
  JournalEntry,
  Media,
  MediaUploadInput,
  MediaUploadResult,
  Memory,
  MemoryWithTrip,
  PartialActivityInput,
  PartialMediaInput,
  PartialMemoryInput,
  PartialStopInput,
  PartialTripInput,
  PlaceNote,
  PlacePhoto,
  PointWeather,
  Proposal,
  PublicSharePayload,
  PublicUnlockResult,
  ResearchResult,
  ReviewNote,
  RouteValidation,
  StopNearby,
  ShareLink,
  ShareLinkSummary,
  StayFilters,
  StaysResponse,
  LogResponse,
  Stop,
  Trip,
  TripSummary,
  TripWeather,
  AgentStreamHandlers,
  AddBucketToTripInput,
  AddBucketToTripResult,
  BucketMatch,
  BucketPlace,
  BucketSource,
  CreateBucketPlaceInput,
  PartialBucketPlaceInput,
  MapPlace,
  AlternativeStop,
  ProposalSource,
  RemoveStopResult,
  SwapStopResult,
  NavHints,
} from './types';
import type { Post, PatchPostInput, PostFormat } from '../features/posts/types';

export class ApiError extends Error {
  code: string;
  status: number;
  constructor(status: number, body: ApiErrorBody) {
    super(body?.error?.message ?? 'Request failed');
    this.name = 'ApiError';
    this.code = body?.error?.code ?? 'unknown';
    this.status = status;
  }
}

async function request<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: init?.body && !(init.body instanceof FormData)
      ? { 'Content-Type': 'application/json', ...(init?.headers ?? {}) }
      : init?.headers,
    ...init,
  });

  if (res.status === 204) {
    return undefined as T;
  }

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const body = isJson ? await res.json().catch(() => undefined) : undefined;

  if (!res.ok) {
    throw new ApiError(res.status, body as ApiErrorBody);
  }
  return body as T;
}

function json(data: unknown): RequestInit {
  return { body: JSON.stringify(data) };
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const getUsers = () => request<AuthUserSummary[]>('/api/auth/users');

export const login = (userId: string, pin: string) =>
  request<void>('/api/auth/login', { method: 'POST', ...json({ userId, pin }) });

export const logout = () => request<void>('/api/auth/logout', { method: 'POST' });

export const getMe = () => request<AuthUser>('/api/auth/me');

// ---------------------------------------------------------------------------
// Trips
// ---------------------------------------------------------------------------

export const getTrips = () => request<TripSummary[]>('/api/trips');

export const createTrip = (input: CreateTripInput) =>
  request<Trip>('/api/trips', { method: 'POST', ...json(input) });

export const getTrip = (id: string) => request<Trip>(`/api/trips/${id}`);

export const patchTrip = (id: string, input: PartialTripInput) =>
  request<Trip>(`/api/trips/${id}`, { method: 'PATCH', ...json(input) });

export const deleteTrip = (id: string) =>
  request<void>(`/api/trips/${id}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Stays — the flat cross-trip ledger
// ---------------------------------------------------------------------------

export const getStays = (filters: StayFilters = {}) => {
  const qs = new URLSearchParams();
  if (filters.from) qs.set('from', filters.from);
  if (filters.to) qs.set('to', filters.to);
  if (filters.historical !== undefined) qs.set('historical', String(filters.historical));
  if (filters.limit !== undefined) qs.set('limit', String(filters.limit));
  const q = qs.toString();
  return request<StaysResponse>(`/api/stays${q ? `?${q}` : ''}`);
};

/** The Log's single fetch: the whole cross-trip timeline in one page. Filtering is deliberately
 *  client-side (see routes/log.ts) so chips are instant. */
export const getLog = (limit?: number) =>
  request<LogResponse>(`/api/log${limit === undefined ? '' : `?limit=${limit}`}`);

// ---------------------------------------------------------------------------
// Approval workflow — research, proposals, review (docs/ITERATION-2.md §3)
// ---------------------------------------------------------------------------

export const researchTrip = (tripId: string) =>
  request<ResearchResult>(`/api/trips/${tripId}/research`, { method: 'POST' });

export const getProposals = (tripId: string) =>
  request<{ proposals: Proposal[] }>(`/api/trips/${tripId}/proposals`).then((r) => r.proposals ?? []);

export const approveProposal = (id: string) =>
  request<ApproveProposalResult>(`/api/proposals/${id}/approve`, { method: 'POST' });

export const declineProposal = (id: string) =>
  request<{ proposal: Proposal }>(`/api/proposals/${id}/decline`, { method: 'POST' }).then((r) => r.proposal);

export const requestTripReview = (tripId: string) =>
  request<Trip>(`/api/trips/${tripId}/request-review`, { method: 'POST' });

export const approveTrip = (tripId: string) =>
  request<Trip>(`/api/trips/${tripId}/approve-trip`, { method: 'POST' });

// ---------------------------------------------------------------------------
// Review notes — what one of them thinks about a trip the other one drafted.
// Orthogonal to the approval flow above: writable at any approval status.
// ---------------------------------------------------------------------------

export const getReviewNotes = (tripId: string, status?: 'open' | 'resolved' | 'all') =>
  request<{ notes: ReviewNote[] }>(
    `/api/trips/${tripId}/review-notes${status ? `?status=${status}` : ''}`
  ).then((r) => r.notes ?? []);

/** Every open note across every trip — "what is waiting on me". Carries `tripTitle` per note. */
export const getOpenReviewNotes = () =>
  request<{ notes: ReviewNote[] }>('/api/review-notes/open').then((r) => r.notes ?? []);

export const addReviewNote = (tripId: string, body: string, stopId?: string | null) =>
  request<ReviewNote>(`/api/trips/${tripId}/review-notes`, {
    method: 'POST',
    body: JSON.stringify({ body, stopId: stopId ?? null }),
  });

export const resolveReviewNote = (id: string, resolution?: string) =>
  request<ReviewNote>(`/api/review-notes/${id}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ resolution: resolution ?? '' }),
  });

export const reopenReviewNote = (id: string) =>
  request<ReviewNote>(`/api/review-notes/${id}/reopen`, { method: 'POST' });

export const deleteReviewNote = (id: string) =>
  request<void>(`/api/review-notes/${id}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Stops & route validation
// ---------------------------------------------------------------------------

/** `override` accepts THIS stop's own hard rule violations (no full hookups, not pet-friendly),
 *  recording the waived codes on the row; `force` bypasses only the successor-leg 409 guard.
 *  Both mean a human said "do it anyway" — never send them unprompted (see docs/API.md). */
export interface StopWriteOptions {
  override?: boolean;
  force?: boolean;
}

function writeQuery(opts: StopWriteOptions = {}): string {
  const qs = new URLSearchParams();
  if (opts.override) qs.set('override', 'true');
  if (opts.force) qs.set('force', 'true');
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export const addStop = (tripId: string, input: CreateStopInput, opts: StopWriteOptions = {}) =>
  request<Stop>(`/api/trips/${tripId}/stops${writeQuery(opts)}`, { method: 'POST', ...json(input) });

export const patchStop = (id: string, input: PartialStopInput, opts: StopWriteOptions = {}) =>
  request<Stop>(`/api/stops/${id}${writeQuery(opts)}`, { method: 'PATCH', ...json(input) });

export const deleteStop = (id: string, opts: StopWriteOptions = {}) =>
  request<void>(`/api/stops/${id}${writeQuery(opts)}`, { method: 'DELETE' });

// Road edits (docs/ROAD-EDITS.md §5) — remove, alternatives, swap. One tap, either human, no
// approval; the server posts the chat line.
export const removeStop = (id: string, opts: StopWriteOptions = {}) =>
  request<RemoveStopResult>(`/api/stops/${id}/remove${writeQuery(opts)}`, { method: 'POST' });

export const getAlternatives = (stopId: string) =>
  request<{ alternatives: Proposal[] }>(`/api/stops/${stopId}/alternatives`).then((r) => r.alternatives ?? []);

export const addAlternative = (
  stopId: string,
  input: { payload: AlternativeStop; rationale?: string; source?: ProposalSource; rank?: number | null },
) => request<Proposal>(`/api/stops/${stopId}/alternatives`, { method: 'POST', ...json(input) });

export const getNavHints = (stopId: string) => request<NavHints>(`/api/stops/${stopId}/nav`);

export const swapStop = (stopId: string, proposalId: string, opts: StopWriteOptions = {}) =>
  request<SwapStopResult>(`/api/stops/${stopId}/swap${writeQuery(opts)}`, { method: 'POST', ...json({ proposalId }) });

export interface PlaceCandidate {
  lat: number;
  lng: number;
  label: string;
  /** Split out of the geocoder's own address details, so picking a candidate fills the stop's
   *  address / city / state instead of leaving them for someone to retype. Any can be ''. */
  address: string;
  city: string;
  state: string;
}

/** Place search for the stop editor. Returns [] for a query under 3 characters or an upstream
 *  hiccup — never throws at the search box for either. */
export const searchPlaces = async (q: string, near?: { lat: number; lng: number }): Promise<PlaceCandidate[]> => {
  const bias = near ? `&near=${near.lat},${near.lng}` : '';
  const res = await request<{ places: PlaceCandidate[]; provider?: 'apple' | 'nominatim' }>(
    `/api/places/search?q=${encodeURIComponent(q)}${bias}`,
  );
  return res.places ?? [];
};

export interface ReverseHit {
  address: string;
  city: string;
  state: string;
  /** The geocoder's own one-line rendering — shown so a wrong pin is obvious before saving. */
  label: string;
}

/** The address for a pin the app already has. Used by the stop editor to fill in a stop created
 *  before the address fields existed. Resolves to null on a 404 ("no address there") and on any
 *  upstream hiccup — a lookup that couldn't happen is never an error the human has to clear. */
export const reverseGeocode = async (lat: number, lng: number): Promise<ReverseHit | null> => {
  try {
    return await request<ReverseHit>(`/api/places/reverse?lat=${lat}&lng=${lng}`);
  } catch {
    return null;
  }
};

// --- Places map layer -------------------------------------------------------
// One request for the whole constellation (see server/src/routes/places.ts): the payload is one
// small object per town, not per stop, so it stays a single fetch even after years of trips.

export const getMapPlaces = async (): Promise<MapPlace[]> => {
  const res = await request<{ places: MapPlace[] }>('/api/places/map');
  return res.places ?? [];
};

/** Star or unstar a town. The place's name/region/point ride along so the star can still name
 *  itself if every record behind the pin is later deleted. Resolves to the place as the SERVER
 *  now has it — repaint from that, not from what the tap assumed. */
export const setPlaceStar = (place: MapPlace, starred: boolean) =>
  request<MapPlace>('/api/places/star', {
    method: 'POST',
    body: JSON.stringify({
      key: place.key,
      starred,
      name: place.name,
      region: place.region,
      lat: place.lat,
      lng: place.lng,
    }),
  });

export const validateRoute = (tripId: string) =>
  request<RouteValidation>(`/api/trips/${tripId}/route/validate`, { method: 'POST' });

/** What's around ONE stop while we're there — festivals on during the stay, and the bucket-list
 *  places within reach of that camp. Asked per stop rather than per trip, so it is only ever
 *  fetched for the stop somebody has actually opened. See docs/ATTRACTIONS.md. */
export const getStopNearby = (stopId: string, withinMiles?: number) =>
  request<StopNearby>(
    `/api/stops/${stopId}/nearby${withinMiles === undefined ? '' : `?withinMiles=${withinMiles}`}`
  );

// ---------------------------------------------------------------------------
// Activities
// ---------------------------------------------------------------------------

export const addActivity = (tripId: string, input: CreateActivityInput) =>
  request<Activity>(`/api/trips/${tripId}/activities`, { method: 'POST', ...json(input) });

export const patchActivity = (id: string, input: PartialActivityInput) =>
  request<Activity>(`/api/activities/${id}`, { method: 'PATCH', ...json(input) });

export const deleteActivity = (id: string) =>
  request<void>(`/api/activities/${id}`, { method: 'DELETE' });

/** Derived on every read from the trip's meal activities (server/src/domain/meals.ts) — there is
 *  no stored list to go stale when a meal is edited. Meals already 'done' are left out. */
export const getShoppingList = (tripId: string) =>
  request<ShoppingList>(`/api/trips/${tripId}/shopping-list`);

export const getMealPresets = () =>
  request<{ presets: MealPreset[] }>('/api/meal-presets').then((r) => r.presets ?? []);

// ---------------------------------------------------------------------------
// Agent chat — ONE shared thread per trip (docs/ITERATION-2.md §3)
// ---------------------------------------------------------------------------

/** Without `after`, the full history; with it, everything strictly newer —
 * used both for the initial load and the 5s poll while the Scooty screen is
 * open. */
export const getChat = (tripId: string, after?: string) =>
  request<{ messages: ChatMessageDto[] }>(
    `/api/trips/${tripId}/chat${after ? `?after=${encodeURIComponent(after)}` : ''}`,
  ).then((r) => r.messages ?? []);

/** Plain human message — no agent turn. Saved instantly; the server
 * auto-extracts @jeff/@elisa/@scooty into `mentions` in addition to whatever
 * the client already parsed client-side for optimistic highlighting. */
export const postChatMessage = (tripId: string, input: { content: string; mentions?: string[] }) =>
  request<ChatMessageDto>(`/api/trips/${tripId}/chat/messages`, { method: 'POST', ...json(input) });

/** Upserts this caller's read cursor for the trip's thread — marks
 * everything up to now as read (powers the Scooty tab's unread badge). */
export const postChatRead = (tripId: string) =>
  request<{ ok: true }>(`/api/trips/${tripId}/chat/read`, { method: 'POST' });

/** Unread counts per trip + whether the caller was @mentioned — polled by
 * BottomNav (~20s + on focus) to badge the Scooty tab. */
export const getChatUnread = () => request<ChatUnreadResponse>('/api/chat/unread');

/**
 * Streams a message to the trip agent over SSE. Parses the exact event
 * names from docs/API.md: delta, tool_start, tool_result, done.
 *
 * Every message in the thread goes through here now — Scooty reads them all and decides whether
 * to answer, so a turn may legitimately end with `done: { silent: true }` and no agent reply.
 * `addressed` overrides that judgment for sends the human aimed at Scooty by some route other
 * than typing @scooty (the suggestion chips); the server detects a typed @scooty itself.
 * Returns an abort() function to cancel the in-flight stream.
 */
export function streamAgent(
  tripId: string,
  message: string,
  handlers: AgentStreamHandlers,
  mentions?: string[],
  addressed?: boolean,
): { abort: () => void } {
  const controller = new AbortController();

  (async () => {
    try {
      const res = await fetch(`/api/trips/${tripId}/agent`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify({
          message,
          ...(mentions && mentions.length > 0 ? { mentions } : {}),
          ...(addressed ? { addressed: true } : {}),
        }),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        const body = await res.json().catch(() => undefined);
        throw new ApiError(res.status, body as ApiErrorBody);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE frames are separated by a blank line.
        let sepIndex: number;
        while ((sepIndex = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, sepIndex);
          buffer = buffer.slice(sepIndex + 2);
          dispatchFrame(frame, handlers);
        }
      }
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      handlers.onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  })();

  return { abort: () => controller.abort() };
}

function dispatchFrame(frame: string, handlers: AgentStreamHandlers) {
  let eventName = 'message';
  const dataLines: string[] = [];

  for (const line of frame.split('\n')) {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim());
    }
  }

  if (dataLines.length === 0) return;
  let data: unknown;
  try {
    data = JSON.parse(dataLines.join('\n'));
  } catch {
    return;
  }

  switch (eventName) {
    case 'delta':
      handlers.onDelta?.(data as never);
      break;
    case 'tool_start':
      handlers.onToolStart?.(data as never);
      break;
    case 'tool_result':
      handlers.onToolResult?.(data as never);
      break;
    case 'done':
      handlers.onDone?.(data as never);
      break;
  }
}

// ---------------------------------------------------------------------------
// Media
// ---------------------------------------------------------------------------

export async function uploadMedia(
  tripId: string,
  input: MediaUploadInput,
): Promise<MediaUploadResult> {
  const form = new FormData();
  form.set('file', input.file);
  form.set('mode', input.mode);
  if (input.stopId) form.set('stopId', input.stopId);
  if (input.caption) form.set('caption', input.caption);
  if (input.peopleTags) form.set('peopleTags', JSON.stringify(input.peopleTags));
  if (input.takenAt) form.set('takenAt', input.takenAt);

  return request<MediaUploadResult>(`/api/trips/${tripId}/media`, {
    method: 'POST',
    body: form,
  });
}

export const getMedia = (tripId: string) => request<Media[]>(`/api/trips/${tripId}/media`);

export const patchMedia = (id: string, input: PartialMediaInput) =>
  request<Media>(`/api/media/${id}`, { method: 'PATCH', ...json(input) });

export const deleteMedia = (id: string) =>
  request<void>(`/api/media/${id}`, { method: 'DELETE' });

export const mediaFileUrl = (id: string) => `/media/${id}/file`;
export const mediaThumbUrl = (id: string) => `/media/${id}/thumb`;

// ---------------------------------------------------------------------------
// Memories — text + 0..n photos, the unified journal/memories model
// (docs/ITERATION-2.md §1/§3)
// ---------------------------------------------------------------------------

export const createMemory = (tripId: string, input: CreateMemoryInput) =>
  request<Memory>(`/api/trips/${tripId}/memories`, { method: 'POST', ...json(input) });

export const getAllMemories = () =>
  request<{ memories: MemoryWithTrip[] }>('/api/memories').then((r) => r.memories ?? []);

export const getTripMemories = (tripId: string) =>
  request<{ memories: Memory[] }>(`/api/trips/${tripId}/memories`).then((r) => r.memories ?? []);

export const patchMemory = (id: string, input: PartialMemoryInput) =>
  request<Memory>(`/api/memories/${id}`, { method: 'PATCH', ...json(input) });

export const deleteMemory = (id: string) =>
  request<void>(`/api/memories/${id}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Bucket list
// ---------------------------------------------------------------------------

export const getBucketList = (opts: { includeVisited?: boolean; source?: BucketSource } = {}) => {
  const qs = new URLSearchParams();
  if (opts.includeVisited === false) qs.set('includeVisited', 'false');
  if (opts.source) qs.set('source', opts.source);
  const query = qs.toString();
  return request<{ places: BucketPlace[] }>(`/api/bucket-list${query ? `?${query}` : ''}`).then(
    (r) => r.places ?? []
  );
};

export const createBucketPlace = (input: CreateBucketPlaceInput) =>
  request<BucketPlace>('/api/bucket-list', { method: 'POST', ...json(input) });

export const patchBucketPlace = (id: string, input: PartialBucketPlaceInput) =>
  request<BucketPlace>(`/api/bucket-list/${id}`, { method: 'PATCH', ...json(input) });

export const deleteBucketPlace = (id: string) =>
  request<void>(`/api/bucket-list/${id}`, { method: 'DELETE' });

/** Shopping: turn a bucket-list place into a real activity on a trip. An activity, not a stop —
 *  see docs/API.md "Bucket list" for why. */
export const addBucketPlaceToTrip = (id: string, input: AddBucketToTripInput) =>
  request<AddBucketToTripResult>(`/api/bucket-list/${id}/add-to-trip`, { method: 'POST', ...json(input) });

/** Which bucket-list places this trip's route goes near. Empty array on a trip with no stops. */
export const getTripBucketMatches = (tripId: string) =>
  request<{ tripId: string; matches: BucketMatch[] }>(`/api/trips/${tripId}/bucket-list`).then(
    (r) => r.matches ?? []
  );

/** The same question for ONE pin rather than a saved route: which bucket-list places are within
 *  reach of these coordinates. The stop editor asks it about the stop being written, so the
 *  picker offers what is near where the rig will actually be — not the whole standing list. */
export const getBucketNearby = (lat: number, lng: number, within?: number) => {
  const qs = new URLSearchParams({ lat: String(lat), lng: String(lng) });
  if (within != null) qs.set('within', String(within));
  return request<{ matches: BucketMatch[] }>(`/api/bucket-list/nearby?${qs.toString()}`).then(
    (r) => r.matches ?? []
  );
};

// ---------------------------------------------------------------------------
// Journal — an ARCHIVE (docs/PLACE-NOTES.md §8)
//
// No generateJournal any more: the endpoint answers 410 `generator_retired`, and a client
// function for it would exist only to produce that error. Read and edit remain, because the
// entries it already wrote are real rows.
// ---------------------------------------------------------------------------

export const getJournal = (tripId: string) =>
  request<JournalEntry[]>(`/api/trips/${tripId}/journal`);

// ---------------------------------------------------------------------------
// Place notes — "About this place" (docs/PLACE-NOTES.md)
// ---------------------------------------------------------------------------

/** One fetch per trip, not per stop. Notes the sweep hasn't found, and notes somebody dismissed,
 *  are simply absent — the caller renders nothing for a stop with no entry here. */
export const getTripPlaceNotes = (tripId: string) =>
  request<PlaceNote[]>(`/api/trips/${tripId}/place-notes`);

/** "Not this place" — the ranker matched the wrong article. A soft dismissal server-side: the row
 *  survives so the background sweep can't cheerfully re-add the same wrong note an hour later. */
export const dismissPlaceNote = (stopId: string) =>
  request<void>(`/api/stops/${stopId}/place-note`, { method: 'DELETE' });

/** Human corrections to a generated entry — title, bodyMd, heroMediaId only. Deliberately cannot
 *  touch `status` (publish is its own dedicated action) or `generatedAt` (docs/API.md). */
export interface PartialJournalEntryInput {
  title?: string;
  bodyMd?: string;
  heroMediaId?: string | null;
}

export const patchJournalEntry = (tripId: string, entryId: string, input: PartialJournalEntryInput) =>
  request<JournalEntry>(`/api/trips/${tripId}/journal/${entryId}`, { method: 'PATCH', ...json(input) });

// ---------------------------------------------------------------------------
// Sharing & public
// ---------------------------------------------------------------------------

export const createShare = (tripId: string, input: CreateShareInput) =>
  request<ShareLinkSummary>(`/api/trips/${tripId}/shares`, { method: 'POST', ...json(input) });

export const getShares = (tripId: string) =>
  request<ShareLink[]>(`/api/trips/${tripId}/shares`);

export const deleteShare = (id: string) =>
  request<void>(`/api/shares/${id}`, { method: 'DELETE' });

export const unlockShare = (slug: string, passcode: string) =>
  request<PublicUnlockResult>(`/api/public/shares/${slug}/unlock`, {
    method: 'POST',
    ...json({ passcode }),
  });

export const getSharePayload = (slug: string, shareToken: string) =>
  request<PublicSharePayload>(`/api/public/shares/${slug}/payload`, {
    headers: { 'x-share-token': shareToken },
  });

export const postGuestbook = (slug: string, shareToken: string, input: GuestbookEntryInput) =>
  request<GuestbookEntry>(`/api/public/shares/${slug}/guestbook`, {
    method: 'POST',
    headers: { 'x-share-token': shareToken },
    ...json(input),
  });

// ---------------------------------------------------------------------------
// Calendar
// ---------------------------------------------------------------------------

export const calendarIcsUrl = (tripId: string, icsKey: string) =>
  `/api/trips/${tripId}/calendar.ics?key=${encodeURIComponent(icsKey)}`;

// The URL a human hands to Google/Apple Calendar. `webcal:` (not https:) is what makes those
// apps offer to SUBSCRIBE — an https link downloads a one-time snapshot that never updates
// again, which is the wrong thing for a trip that is still being planned. Absolute by
// necessity: the calendar app fetches it, not the browser that copied it.
//
// The key is HMAC(appSecret, "ics:" + tripId) — stable and NOT revocable per trip: rotating it
// means rotating the app secret, which invalidates every trip's feed at once. That is an
// acceptable trade for two humans; if these links ever go wider than Jeff and Elisa, this wants
// a real per-trip key column instead.
export const calendarWebcalUrl = (tripId: string, icsKey: string) =>
  `webcal://${window.location.host}${calendarIcsUrl(tripId, icsKey)}`;

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Weather (docs/API.md "Weather") — forecast, what actually happened, or the
// typical week, per stop. Never throws the trip page away when it fails: the
// caller treats an error as "no strips", not as a broken screen.
// ---------------------------------------------------------------------------

export const getTripWeather = (tripId: string) =>
  request<TripWeather>(`/api/trips/${tripId}/weather`);

export const getPointWeather = (lat: number, lng: number, start: string, end?: string) =>
  request<PointWeather>(
    `/api/weather?lat=${lat}&lng=${lng}&start=${encodeURIComponent(start)}` +
      (end ? `&end=${encodeURIComponent(end)}` : ''),
  );

// Places — free Wikipedia-sourced placeholder cover photos (docs/API.md "Places")
// ---------------------------------------------------------------------------

/** 404 (a definitive "nothing free found nearby") and 503 (upstream failure/cooldown — retry
 * later, don't treat it as a permanent miss) both surface as a thrown ApiError distinguished by
 * `.status` — callers fall back to the poster gradient either way, but should only ever cache a
 * 404 as "never ask again" (see TripCard.tsx usePrimaryStopPhoto). */
export const getPlacePhoto = (lat: number, lng: number, name: string) =>
  request<PlacePhoto>(
    `/api/places/photo?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}&name=${encodeURIComponent(name)}`,
  );

// ---------------------------------------------------------------------------
// Posts — public blog, minimal post screen (§14 ship item 5) and the in-app
// composer (§14 ship item 8)
// ---------------------------------------------------------------------------

export const getPosts = () =>
  request<{ posts: Post[] }>('/api/posts').then((r) => r.posts ?? []);

export const getPost = (id: string) => request<Post>(`/api/posts/${id}`);

export const patchPost = (id: string, input: PatchPostInput) =>
  request<Post>(`/api/posts/${id}`, { method: 'PATCH', ...json(input) });

/** Tap-to-edit a single text block in place (§5, §14.2) — the dedicated
 * endpoint that regenerates draft_text server-side. Never write a text
 * block's prose through patchPost's generic `body` field instead. */
export const patchPostBlock = (id: string, index: number, md: string) =>
  request<Post>(`/api/posts/${id}/blocks/${index}`, { method: 'PATCH', ...json({ md }) });

export const publishPost = (id: string) =>
  request<Post>(`/api/posts/${id}/publish`, { method: 'POST' });

export const unpublishPost = (id: string) =>
  request<Post>(`/api/posts/${id}/unpublish`, { method: 'POST' });

/** Create an empty draft (docs/BLOG.md §14). The composer creates first and
 * writes second, so every photo it uploads has a post id to be scoped to. */
export const createPost = (input: { draftText?: string; tripId?: string | null; format?: PostFormat } = {}) =>
  request<Post>('/api/posts', { method: 'POST', ...json(input) });

export const deletePost = (id: string) =>
  request<void>(`/api/posts/${id}`, { method: 'DELETE' });

/** One photo, straight into this post's tray (§4.1) — the composer's photo
 * door. One file per request on purpose: per-photo progress, and one bad file
 * out of five doesn't take the other four down with it. */
export async function uploadPostPhoto(postId: string, file: File): Promise<{ mediaId: string; post: Post }> {
  const form = new FormData();
  form.set('file', file);
  return request<{ mediaId: string; post: Post }>(`/api/posts/${postId}/media`, {
    method: 'POST',
    body: form,
  });
}

/** Detach a tray photo. The composer renumbers the `[photo-N]` markers in the
 * draft text itself afterwards — tray order IS marker order (§4.1). */
export const removeFromTray = (postId: string, mediaId: string) =>
  request<Post>(`/api/posts/${postId}/tray/${mediaId}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------

export const getHealth = () => request<HealthResponse>('/api/health');
